import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useUser } from "@clerk/clerk-react"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { FileText, Upload, BarChart3, Menu, X, Loader2, UploadCloud, LogOut, User } from "lucide-react"
import { api, type ExamPaper } from "@/lib/api"
import { toast } from "sonner"
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels"
import { ExamFormDialog } from "@/components/exam-form-dialog"
import { ExamPaperPreviewDialog } from "@/components/ExamPaperPreviewDialog"
import { ExamPapersTable } from "@/components/ExamPapersTable"
import { PaperFilters } from "@/components/PaperFilters"
import { PaperPreviewPanel } from "@/components/PaperPreviewPanel"
import { PaginationControls } from "@/components/PaginationControls"
import StatsPage from "@/components/StatsPage"

export default function AdminPage() {
  const navigate = useNavigate()
  const { user } = useUser()
  const [papers, setPapers] = useState<ExamPaper[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedYear, setSelectedYear] = useState<string>("")
  const [selectedSubject, setSelectedSubject] = useState<string>("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPaperId, setSelectedPaperId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [activeTab, setActiveTab] = useState("papers")
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewPaper, setPreviewPaper] = useState<ExamPaper | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isMobile, setIsMobile] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editPaper, setEditPaper] = useState<ExamPaper | null>(null)

  // Detect viewport size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (activeTab === "papers") {
      fetchPapers()
    }
  }, [selectedYear, selectedSubject, searchQuery, currentPage, activeTab])

  const fetchPapers = async () => {
    setLoading(true)
    try {
      const response = await api.getPapers({
        page: currentPage,
        per_page: 20,
        year: selectedYear || undefined,
        subject: selectedSubject || undefined,
        search: searchQuery || undefined
      })
      setPapers(response.data)
      setTotalPages(response.total_pages)
    } catch (error) {
      console.error("Failed to fetch papers:", error)
      toast.error("加载试卷失败")
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setSearchQuery("")
    setSelectedYear("")
    setSelectedSubject("")
    setCurrentPage(1)
  }

  const handleDelete = (id: number) => {
    setSelectedPaperId(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedPaperId) return

    setDeleting(true)
    try {
      await api.deletePaper(selectedPaperId)
      toast.success("试卷删除成功")
      setDeleteDialogOpen(false)
      setSelectedPaperId(null)
      // Refresh the list
      fetchPapers()
    } catch (error) {
      console.error("Delete error:", error)
      toast.error("删除失败，请重试")
    } finally {
      setDeleting(false)
    }
  }

  const handleUploadClick = () => {
    navigate("/upload")
  }

  const handleBatchUploadClick = () => {
    navigate("/admin/batch-upload")
  }

  const handleViewClick = (paper: ExamPaper) => {
    setPreviewPaper(paper)
    if (isMobile) {
      setShowPreview(false)
      setPreviewOpen(true)
    } else {
      setShowPreview(true)
      setPreviewOpen(false)
    }
  }

  const handleClosePreview = () => {
    setShowPreview(false)
    setPreviewPaper(null)
  }


  const handleEdit = (paper: ExamPaper) => {
    setEditPaper(paper)
    setEditDialogOpen(true)
  }

  const handleEditSuccess = () => {
    fetchPapers()
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 flex-shrink-0 transform border-r border-border bg-card transition-transform lg:relative lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-14 items-center justify-between border-b border-border px-4 sm:h-16 sm:px-6">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <span className="text-base font-semibold text-foreground sm:text-lg">试卷管理平台</span>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 p-4">
            <button
              onClick={() => {
                setActiveTab("papers")
                setSidebarOpen(false)
              }}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${activeTab === "papers"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
            >
              <FileText className="h-4 w-4" />
              <span>试卷列表</span>
            </button>
            <button
              onClick={() => {
                setActiveTab("stats")
                setSidebarOpen(false)
              }}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${activeTab === "stats"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
            >
              <BarChart3 className="h-4 w-4" />
              <span>数据统计</span>
            </button>
          </nav>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex grow flex-col max-w-full overflow-hidden min-h-0">
        {/* Top Navigation */}
        <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4 sm:h-16 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button className="lg:hidden flex-shrink-0" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-base font-semibold text-foreground sm:text-lg lg:text-xl truncate">
              {activeTab === "papers" ? "试卷列表" : "数据统计"}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {user && (
              <div className="hidden md:flex items-center gap-2 mr-2 px-3 py-1 rounded-md bg-accent/50">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {user.primaryEmailAddress?.emailAddress || user.username || 'User'}
                </span>
              </div>
            )}
            <Button
              variant="outline"
              className="h-8 gap-1 px-2 sm:h-9 sm:gap-2 sm:px-4 lg:h-10"
              size="sm"
              onClick={() => navigate('/logout')}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline text-sm">退出</span>
            </Button>
            <Button variant="outline" className="h-8 gap-1 px-2 sm:h-9 sm:gap-2 sm:px-4 lg:h-10" size="sm" onClick={handleBatchUploadClick}>
              <UploadCloud className="h-4 w-4" />
              <span className="hidden sm:inline text-sm">批量上传</span>
              <span className="sm:hidden text-xs">批量</span>
            </Button>
            <Button className="h-8 gap-1 px-2 sm:h-9 sm:gap-2 sm:px-4 lg:h-10" size="sm" onClick={handleUploadClick}>
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline text-sm">上传试卷</span>
              <span className="sm:hidden text-xs">上传</span>
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex grow flex-col overflow-hidden max-w-full min-h-0">
          {activeTab === "papers" ? (
            <div className="flex flex-1 flex-col overflow-hidden p-3 sm:p-4 lg:p-6 min-h-0">
              {/* Filter Bar */}
              <div className="mb-4 sm:mb-6 flex-shrink-0">
                <PaperFilters
                  searchQuery={searchQuery}
                  selectedYear={selectedYear}
                  selectedSubject={selectedSubject}
                  onSearchChange={(value) => {
                    setSearchQuery(value)
                    setCurrentPage(1)
                  }}
                  onYearChange={(value) => {
                    setSelectedYear(value)
                    setCurrentPage(1)
                  }}
                  onSubjectChange={(value) => {
                    setSelectedSubject(value)
                    setCurrentPage(1)
                  }}
                  onClearFilters={clearFilters}
                />
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              {/* Papers Table - Split Panel Layout for Desktop */}
              {!loading && papers.length > 0 ? (
                showPreview && !isMobile ? (
                  <PanelGroup direction="horizontal" className="grow rounded-lg border border-border overflow-hidden min-h-0">
                    {/* Left Panel - Table */}
                    <Panel defaultSize={50} minSize={30} maxSize={70}>
                      <div className="h-full overflow-auto bg-card">
                        <ExamPapersTable
                          papers={papers}
                          selectedPaperId={previewPaper?.id}
                          onView={handleViewClick}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                        <PaginationControls
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    </Panel>

                    {/* Resize Handle */}
                    <PanelResizeHandle className="w-2 bg-border hover:bg-primary/20 transition-colors" />

                    {/* Right Panel - PDF Preview */}
                    <Panel defaultSize={50} minSize={30}>
                      <PaperPreviewPanel
                        paper={previewPaper}
                        onClose={handleClosePreview}
                      />
                    </Panel>
                  </PanelGroup>
                ) : (
                  <div className="flex-1 overflow-auto">
                    <div className="rounded-lg border border-border bg-card max-w-full">
                      <ExamPapersTable
                        papers={papers}
                        selectedPaperId={previewPaper?.id}
                        onView={handleViewClick}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </div>
                )
              ) : !loading && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-16 px-4">
                    <FileText className="mb-4 h-12 w-12 text-muted-foreground" />
                    <p className="mb-4 text-center text-muted-foreground">
                      {(selectedYear || selectedSubject || searchQuery) ? "未找到匹配的试卷" : "还没有上传试卷"}
                    </p>
                    {(selectedYear || selectedSubject || searchQuery) ? (
                      <Button variant="outline" onClick={clearFilters}>
                        清除筛选条件
                      </Button>
                    ) : (
                      <Button className="gap-2" onClick={handleUploadClick}>
                        <Upload className="h-4 w-4" />
                        上传第一份试卷
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <StatsPage />
          )}
        </main>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除试卷？</AlertDialogTitle>
            <AlertDialogDescription>删除后无法恢复，确定要删除这份试卷吗？</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  删除中...
                </>
              ) : (
                "确认删除"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <ExamPaperPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} paper={previewPaper} />

      {/* Edit Dialog */}
      <ExamFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        examData={editPaper || undefined}
        onSuccess={handleEditSuccess}
      />
    </div>
  )
}
